import React, { useState } from "react";
import classnames from "classnames";

import { getDeals } from "@/api/requests/getDeals";
import { Action, Category } from "@/constants/events";
import { Coordinates } from "@/custom-types/Coordinates";
import { Deal } from "@/custom-types/Deals";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useDispatch, useSelector } from "@/redux/hooks";
import { getCoordinates } from "@/redux/selectors/location";
import { getUserId } from "@/redux/selectors/user";
import { dealCardUpdateMedRec } from "@/utils/dealCardUpdateMedRec";
import { getDispensaryUrl } from "@/utils/dispensaryUrls";
import { getRoundedDistance } from "@/utils/distance";
import { getDealKindLabel } from "@/utils/getDealKindLabel";

import DealCard, { DealCardPlaceholder } from "@/components/botanic//DealCard";
import Button from "@/components/botanic/Button";
import DealsPromosNoResults from "@/components/DealsPromosNoResults";

const DEALS_PER_PAGE = 6;

const DealsResults: React.FC<{
  className?: string;
  coordinates?: Coordinates | null;
  initialDeals: Deal[];
  totalCount: number;
}> = ({ className, coordinates, initialDeals, totalCount }) => {
  const countryCode = useDomainCountryCode();
  const { publishEvent } = useEventTracker();

  const userCoordinates = useSelector(getCoordinates);
  const userId = useSelector(getUserId);

  const [deals, setDeals] = useState<Deal[] | undefined>(initialDeals);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  const handleLoadMoreClick = async () => {
    setIsLoading(true);

    publishEvent({
      action: Action.click,
      category: Category.loadMoreDealsButton,
      label: "",
    });

    const { deals: newDeals } = await getDeals(
      coordinates || userCoordinates,
      countryCode,
      deals?.length,
      DEALS_PER_PAGE,
    );

    setDeals(deals?.length ? [...deals, ...newDeals] : newDeals);
    setIsLoading(false);
  };

  const cardClick = async (deal: Deal) => {
    const {
      dispensary: { id: dispensaryId, distanceMi },
      id: dealId,
    } = deal;

    publishEvent({
      action: Action.click,
      category: Category.dealsMenuBrowseDeals,
      dealId,
      dispensaryDistance: getRoundedDistance(distanceMi, countryCode),
      dispensaryId: dispensaryId,
      label: getDealKindLabel(deal),
      userId: userId as number,
    });

    // Med/rec deals will have medical and/or recreational properties, check if we need to update the user preference
    if (deal.isMedical || deal.isRecreational) {
      await dealCardUpdateMedRec(deal, dispatch);
    }
  };

  const cardImpression = (deal: Deal) => {
    const {
      dispensary: { id: dispensaryId, distanceMi },
      id: dealId,
    } = deal;

    publishEvent({
      action: Action.impression,
      category: Category.dealsMenuBrowseDeals,
      dealId,
      dispensaryDistance: getRoundedDistance(distanceMi, countryCode),
      dispensaryId: dispensaryId,
      label: getDealKindLabel(deal),
      nonInteractionEvent: true,
      userId: userId as number,
    });
  };

  const currentCount = deals?.length || 0;

  return (
    <div className={classnames(className)}>
      <h2 className="heading--m mb-md">Find a 420 deal today</h2>

      <div className="row">
        {deals?.length
          ? deals.map((deal: Deal) => {
              const {
                title,
                dispensary: { slug },
              } = deal;

              const isDealCBD = !deal.isMedical && !deal.isRecreational;
              const dealType =
                deal.isMedical && !deal.isRecreational ? "Med" : "Rec";

              const dealUrl = getDispensaryUrl({
                countryCode,
                query: {
                  deal_title: [title],
                  ...(!isDealCBD && { menu_type: dealType }),
                },
                slug,
                tab: "menu",
              });

              return (
                <div key={deal.id} className="col md:col-1/3 mb-md">
                  <DealCard
                    data-testid="results-deal-card"
                    deal={{
                      ...deal,
                      dealUrl,
                    }}
                    showFulfillment={true}
                    countryCode={countryCode}
                    className="rounded h-full overflow-hidden shadow-low"
                    onClick={() => cardClick(deal)}
                    onLoad={() => cardImpression(deal)}
                  />
                </div>
              );
            })
          : deals !== undefined && (
              <div className="col">
                <DealsPromosNoResults type="deals" className="h-[390px]" />
              </div>
            )}

        {(deals === undefined || isLoading) &&
          Array(
            totalCount - currentCount >= DEALS_PER_PAGE
              ? DEALS_PER_PAGE
              : totalCount - currentCount,
          )
            .fill(null)
            .map((_, i) => (
              <div
                className="col md:col-1/3 mb-md"
                key={`deals-results-placeholder-${i}`}
              >
                <DealCardPlaceholder />
              </div>
            ))}
      </div>

      {totalCount > DEALS_PER_PAGE && currentCount < totalCount && (
        <Button
          className={classnames("mt-lg", {
            "bg-light-grey": isLoading,
          })}
          data-testid="load-more-deals"
          disabled={isLoading}
          onClick={handleLoadMoreClick}
        >
          load more deals
        </Button>
      )}
    </div>
  );
};

export default DealsResults;
